/**
 * Content page — Module 3 "Stratégie de contenu".
 *
 * Shows Inea's editorial strategy: the content pillars and a batch of
 * ready-to-produce post ideas, each with its hook and CTA (copyable).
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Loader2, CalendarDays, Sparkles, RefreshCw, Copy, Check, Layers, ArrowRight, Send } from 'lucide-react';
import { useContent, type ContentPlan, type PostIdea } from '@/hooks/useContent';
import { useFeatures } from '@/hooks/useFeatures';
import { PublishSheet } from '@/components/publish/PublishSheet';
import { ContentToRegenerate } from '@/components/ContentToRegenerate';
export default function ContentPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { plan, isLoading, generate } = useContent();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="animate-spin text-pink" size={30} />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[900px] px-5 py-10 sm:px-8">
      <header className="mb-8 flex items-center gap-3.5">
        <div className="flex-1">
          <p className="font-mono text-[11px] font-700 uppercase tracking-[0.18em] text-pink">
            {t("content.kicker")}
          </p>
          <h1 className="font-display text-[clamp(22px,3vw,30px)] font-800 tracking-[-0.025em]">
            {t("content.title")}
          </h1>
        </div>
        {plan && (
          <button
            onClick={() => generate.mutate()}
            disabled={generate.isPending}
            className="btn-ghost px-4 py-2.5 text-[13px] disabled:opacity-60"
          >
            {generate.isPending ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <RefreshCw size={14} />
            )}
            Régénérer
          </button>
        )}
      </header>

      {!plan ? (
        <EmptyState
          generating={generate.isPending}
          error={generate.error?.message ?? null}
          onGenerate={() => generate.mutate()}
        />
      ) : plan.stale ? (
        <ContentToRegenerate
          stored={plan.lang ?? 'fr'}
          onRegenerate={() => generate.mutate()}
          busy={generate.isPending}
        />
      ) : (
        <PlanView plan={plan} onAudit={() => navigate('/audit')} />
      )}
    </div>
  );
}

function EmptyState({
  generating,
  error,
  onGenerate,
}: {
  generating: boolean;
  error: string | null;
  onGenerate: () => void;
}) {
  const { t } = useTranslation();
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      className="card-hi rounded-3xl p-9 text-center"
    >
      <span
        className="mx-auto mb-5 inline-flex h-14 w-14 items-center justify-center rounded-2xl text-white"
        style={{ background: 'linear-gradient(135deg, #3b82f6, #ec4899)' }}
      >
        <CalendarDays size={24} />
      </span>
      <h2 className="font-display text-xl font-800 tracking-[-0.02em]">
        {t('content.emptyTitle')}
      </h2>
      <p className="mx-auto mt-2.5 max-w-md text-[14px] leading-[1.6] text-ink/65">
        {t('content.emptyBody')}
      </p>
      {error && (
        <p className="mx-auto mt-4 max-w-md rounded-xl bg-pink/10 px-3.5 py-2.5 text-[13px] font-500 text-pink-deep">
          {error}
        </p>
      )}
      <button
        onClick={onGenerate}
        disabled={generating}
        className="btn-primary mt-6 disabled:opacity-60"
      >
        {generating ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
        {generating ? t('content.generating') : t('content.generate')}
      </button>
    </motion.div>
  );
}

function PlanView({ plan, onAudit }: { plan: ContentPlan; onAudit: () => void }) {
  const { t } = useTranslation();
  const [pillarFilter, setPillarFilter] = useState<string | null>(null);
  const ideas = pillarFilter
    ? plan.post_ideas.filter((p) => p.pillar === pillarFilter)
    : plan.post_ideas;

  return (
    <div className="space-y-5">
      {/* Cadence */}
      {plan.cadence && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-start gap-3 rounded-[22px] bg-gradient-to-br from-blue/[0.06] to-violet/[0.06] p-5"
        >
          <CalendarDays size={18} className="mt-0.5 shrink-0 text-blue" />
          <div>
            <p className="font-mono text-[10px] font-700 uppercase tracking-[0.14em] text-ink/50">
              {t('content.cadence')}
            </p>
            <p className="mt-0.5 text-[14px] leading-[1.55] text-ink/80">{plan.cadence}</p>
          </div>
        </motion.div>
      )}

      {/* Pillars */}
      <motion.section
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.06 }}
        className="card p-6 sm:p-7"
      >
        <div className="mb-4 flex items-center gap-2">
          <Layers size={15} className="text-violet" />
          <h3 className="font-display text-[16px] font-800 tracking-[-0.02em]">
            {t('content.pillars')}
          </h3>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          {plan.pillars.map((p, i) => (
            <button
              key={i}
              onClick={() => setPillarFilter(pillarFilter === p.name ? null : p.name)}
              className={`rounded-2xl border p-4 text-left transition ${
                pillarFilter === p.name
                  ? 'border-pink/50 bg-pink/[0.03]'
                  : 'border-ink/[0.07] hover:border-pink/30'
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="h-3 w-3 rounded-full" style={{ background: p.color }} />
                <span className="font-display text-[14px] font-800 text-ink">{p.name}</span>
              </div>
              <p className="mt-1.5 text-[12.5px] leading-[1.5] text-ink/60">{p.description}</p>
            </button>
          ))}
        </div>
        {pillarFilter && (
          <button
            onClick={() => setPillarFilter(null)}
            className="mt-3 text-[12px] font-700 text-pink transition hover:underline"
          >
            ✕ {t('content.showAllIdeas')}
          </button>
        )}
      </motion.section>

      {/* Post ideas */}
      <div>
        <h3 className="mb-3.5 font-display text-[15px] font-800 tracking-[-0.015em] text-ink/80">
          {pillarFilter ? t('content.ideasFor', { pillar: pillarFilter }) : t('content.postIdeas')}
          <span className="ml-2 font-mono text-[11px] font-700 text-ink/40">{ideas.length}</span>
        </h3>
        <div className="grid gap-3.5 sm:grid-cols-2">
          {ideas.map((idea, i) => (
            <PostIdeaCard
              key={i}
              idea={idea}
              color={plan.pillars.find((p) => p.name === idea.pillar)?.color ?? '#a855f7'}
              delay={i * 0.04}
            />
          ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-3 pt-2">
        <button onClick={onAudit} className="btn-primary">
          <ArrowRight size={16} /> Trouver des prospects
        </button>
      </div>
    </div>
  );
}

function PostIdeaCard({ idea, color, delay }: { idea: PostIdea; color: string; delay: number }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  // Lot 2 — publier l'idée directement (légende = titre + accroche + CTA).
  const { publishing_enabled: publishingEnabled } = useFeatures();
  const [publishOpen, setPublishOpen] = useState(false);

  function copy(): void {
    const txt = `${idea.title}\n\nAccroche : ${idea.hook}\n\nCTA : ${idea.cta}`;
    void navigator.clipboard.writeText(txt).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.4 }}
      className="card group flex flex-col p-5"
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <span
            className="rounded-full px-2 py-0.5 text-[10px] font-700"
            style={{ background: `${color}1a`, color }}
          >
            {idea.pillar}
          </span>
          <span className="rounded-full bg-ink/[0.05] px-2 py-0.5 font-mono text-[9.5px] font-700 uppercase tracking-[0.1em] text-ink/55">
            {idea.format}
          </span>
        </div>
        <span className="font-mono text-[10px] font-700 uppercase tracking-[0.1em] text-ink/40">
          {idea.day}
        </span>
      </div>

      <h4 className="font-display text-[15px] font-800 leading-[1.25] tracking-[-0.015em] text-ink">
        {idea.title}
      </h4>

      <div className="mt-2.5 rounded-xl bg-ink/[0.03] p-3">
        <p className="font-mono text-[9px] font-700 uppercase tracking-[0.12em] text-ink/40">
          Accroche
        </p>
        <p className="mt-0.5 text-[13px] leading-[1.5] text-ink/80">« {idea.hook} »</p>
      </div>

      <div className="mt-2 flex items-center justify-between gap-2">
        <p className="flex-1 text-[12px] text-ink/55">
          <span className="font-700 text-pink">{t('common.ctaLabel')}</span> {idea.cta}
        </p>
        <button
          onClick={copy}
          className="inline-flex shrink-0 items-center gap-1 rounded-lg bg-ink/[0.05] px-2 py-1 text-[11px] font-700 text-ink/60 transition hover:bg-pink/10 hover:text-pink"
        >
          {copied ? <Check size={11} /> : <Copy size={11} />}
          {copied ? 'Copié' : 'Copier'}
        </button>
        {publishingEnabled && (
          <button
            onClick={() => setPublishOpen(true)}
            className="inline-flex shrink-0 items-center gap-1 rounded-lg bg-pink/10 px-2 py-1 text-[11px] font-700 text-pink transition hover:bg-pink hover:text-white"
          >
            <Send size={11} /> {t('publish.button')}
          </button>
        )}
      </div>
      {publishingEnabled && (
        <PublishSheet
          open={publishOpen}
          onClose={() => setPublishOpen(false)}
          origin="idea"
          originRef={idea.title.slice(0, 200)}
          initialCaption={`${idea.title}\n\n${idea.hook}\n\n${idea.cta}`}
        />
      )}
    </motion.div>
  );
}
